/**
 * Employee self-service payslips.
 *
 * Route: /my-payslips. Any employee can visit and see ONLY the
 * payslips dispatched to them. Backend endpoint
 * `GET /api/payroll-engine/my-payslips` resolves the caller's
 * employee record via user_id and returns dispatched entries.
 */
import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilePdf, ArrowRight } from "@phosphor-icons/react";

export default function MyPayslipsPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payroll-engine/my-payslips");
        setRows(data.payslips || []);
      } catch (e) { toast.error(formatApiError(e)); setRows([]); }
    })();
  }, []);

  const download = (r) => {
    const url = `${API_BASE}/payroll-engine/runs/${r.run_id}/payslip/${r.employee_id}/pdf`;
    fetch(url, { credentials: "include" })
      .then(res => res.blob())
      .then(b => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `payslip-${r.period_end}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="space-y-6" data-testid="my-payslips-page">
      <div>
        <div className="overline mb-1">Payroll</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">My Payslips</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your dispatched payslips — download the PDF to keep for your records.
        </p>
      </div>
      <Card>
        <CardContent className="p-0">
          {rows === null && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
          {rows?.length === 0 && (
            <div className="p-10 text-sm text-muted-foreground text-center">
              No payslips have been dispatched to you yet. Once your admin dispatches
              a payroll run you&apos;ll see it here.
            </div>
          )}
          <div className="divide-y divide-border">
            {rows?.map(r => (
              <div key={`${r.run_id}-${r.employee_id}`} className="flex items-center gap-4 px-5 py-3.5" data-testid={`my-payslip-row-${r.run_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{r.period_start} → {r.period_end}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    Ref: <span className="font-mono">{r.payslip_ref}</span> · Sent {r.dispatched_at ? new Date(r.dispatched_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Net</div>
                  <div className="font-display text-xl font-bold tabular-nums">{fmtMoney(r.net || 0)}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => download(r)} className="gap-1.5" data-testid={`my-payslip-dl-${r.run_id}`}>
                  <FilePdf size={14} weight="duotone" /> PDF <ArrowRight size={12} />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
